const CardTypeRef = require('@/lib/PaymentMethods/CardTypeRef');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const { container } = require('@/container');
const { ucfirst } = require('@/functions/strings');

/**
 * Attempts to determine a card type based on the card number.
 *
 * @param {string|number} cardNumber - The card number (can be full or partial, masked or unmasked)
 *
 * @returns {string|null} - The card type (from CardTypeRef) or null if undetermined.
 */
const getCardTypeFromCardNumber = (cardNumber) => {
    if (!cardNumber) {
        return null;
    }

    // Convert to string and remove any non-digit characters
    const cleanNumber = String(cardNumber).replace(/[^\d]/g, '');

    // Helper function to check if a number starts with any of the provided prefixes
    const startsWithAnyOf = (number, prefixArray) => {
        return prefixArray.some((p) => number.startsWith(p));
    };

    // VISA: Starts with 4
    if (startsWithAnyOf(cleanNumber, ['4'])) {
        return CardTypeRef.VISA;
    }

    // MASTERCARD: Starts with 51-55 or 2221-2720
    if (startsWithAnyOf(cleanNumber, ['51', '52', '53', '54', '55'])
        || (cleanNumber.length >= 4
            && parseInt(cleanNumber.substring(0, 4)) >= 2221
            && parseInt(cleanNumber.substring(0, 4)) <= 2720)) {
        return CardTypeRef.MASTERCARD;
    }

    // AMEX: Starts with 34 or 37
    if (startsWithAnyOf(cleanNumber, ['34', '37'])) {
        return CardTypeRef.AMEX;
    }

    // UNIONPAY: Starts with 62
    if (/^62[0-9]{14,17}$/.test(cleanNumber)) {
        return CardTypeRef.UNIONPAY; // UnionPay: Starts with 62, 16-19 digits.
    }

    // DISCOVER: Starts with 6011, 622126-622925, 644-649, 65
    if (/^6(?:011|5[0-9]{2}|4[4-9][0-9])\d{12,15}$/.test(cleanNumber)) {
        return CardTypeRef.DISCOVER; // Discover: Specific BIN ranges.
    }

    // JCB: Starts with 3528-3589
    if (cleanNumber.length >= 4
        && parseInt(cleanNumber.substring(0, 4)) >= 3528
        && parseInt(cleanNumber.substring(0, 4)) <= 3589) {
        return CardTypeRef.JCB;
    }

    // DINERS: Starts with 300-305, 36, 38-39
    if (startsWithAnyOf(cleanNumber, ['300', '301', '302', '303', '304', '305', '36', '38', '39'])) {
        return CardTypeRef.DINERS;
    }

    // MAESTRO: Starts with 5018, 5020, 5038, 5893, 6304, 6759, 6761, 6762, 6763
    if (startsWithAnyOf(cleanNumber, ['5018', '5020', '5038', '5893', '6304', '6759', '6761', '6762', '6763'])) {
        return CardTypeRef.MAESTRO;
    }

    // LASER: Starts with 6304, 6706, 6771, 6709
    // Laser has some overlap with Maestro. But Laser is not used anymore since 2014 so we don't need to care about it.
    if (startsWithAnyOf(cleanNumber, ['6304', '6706', '6771', '6709'])) {
        return CardTypeRef.LASER;
    }

    // SOLO: Starts with 6334, 6767
    if (startsWithAnyOf(cleanNumber, ['6334', '6767'])) {
        return CardTypeRef.SOLO;
    }

    // If no match found
    return null;
};

/**
 * @param {Payment} payment
 *
 * @return {string}
 */
const getPaymentMethodNameForReceipt = (payment) => {
    const ref = payment.paymentMethod.ref;

    if (ref === PaymentMethodRef.FLEXI_VOUCHER && !payment.changeGiven && payment.tendered < 0) {
        return container.langService.getText('PAYMENT.FLEXI_VOUCHER_REFUND_SHORT');
    }

    if (ref === PaymentMethodRef.FLEXI_VOUCHER) {
        return container.langService.getText('PAYMENT.FLEXI_VOUCHER');
    }

    if (ref === PaymentMethodRef.CASH) {
        return container.langService.getText('PAYMENT.CASH');
    }

    if (ref === PaymentMethodRef.GOCARDLESS) {
        return container.langService.getText('PAYMENT.GOCARDLESS_RECEIPT_NAME');
    }

    return ucfirst(payment.paymentMethod.shortName);
};

module.exports = {
    getCardTypeFromCardNumber,
    getPaymentMethodNameForReceipt,
};
